import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const ContentCtx = createContext({
  content: {},
  ready: false,
  previewing: false,
  refresh: async () => {},
  t: (_k, fb) => fb,
});

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [ready, setReady] = useState(false);
  const [overrides, setOverrides] = useState(null); // null | { [key]: value }

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content/public");
      setContent(data || {});
    } catch (e) {
      // keep fallbacks
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener("neptune:content-updated", onUpdate);
    return () => window.removeEventListener("neptune:content-updated", onUpdate);
  }, [refresh]);

  // Preview mode: iframe listens for overrides posted by the admin PageEditor
  useEffect(() => {
    const sameOrigin = (e) => {
      try { return e.origin === window.location.origin; } catch { return false; }
    };
    const onMessage = (e) => {
      if (!sameOrigin(e)) return;
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "neptune:preview-overrides") {
        setOverrides(d.overrides && typeof d.overrides === "object" ? d.overrides : {});
      } else if (d.type === "neptune:preview-clear") {
        setOverrides(null);
      }
    };
    window.addEventListener("message", onMessage);
    // If embedded in an iframe, signal readiness so parent can push overrides.
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          { type: "neptune:preview-ready" },
          window.location.origin,
        );
      } catch {
        /* cross-origin — ignore */
      }
    }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const t = useCallback(
    (key, fallback = "") => {
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
        const v = overrides[key];
        if (v === undefined || v === null || v === "") return fallback;
        return v;
      }
      const v = content[key];
      if (v === undefined || v === null || v === "") return fallback;
      return v;
    },
    [content, overrides],
  );

  return (
    <ContentCtx.Provider
      value={{ content, ready, previewing: overrides !== null, refresh, t }}
    >
      {children}
    </ContentCtx.Provider>
  );
}

export function useContent() {
  return useContext(ContentCtx);
}
